import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  Card, Form, Row, Col, InputGroup, Button, Toast, Alert,
} from 'react-bootstrap';
import { Link, Element, scroller } from 'react-scroll';

import BackBtn from '../../components/UI/BackBtn';
import MainInfo from '../../components/BuyCreateCards/MainInfo';
import BuyProjResults from '../../components/BuyCreateCards/BuyProjResults';
import BuyKvrsSf from '../../components/BuyCreateCards/BuyKvrsSf';
import BuyFormButtons from '../../components/BuyCreateCards/BuyFormButtons';
import BuyTeos from '../../components/BuyCreateCards/BuyTeos';

import useUserContext from '../../hooks/useUserContext';
import useNavProject from '../../hooks/useNavProject';
import {
  isAdmin, isStratAdmin, isProjectAdmin, isPortfolioAdmin,
} from '../../services/rolesSvc';
import { stepScroll } from '../../helpers';
import useToast from '../../hooks/useToast';
import ModalSendForRevision from '../../components/common/ModalSendForRevision';
import { state as BST, status as BS } from '../../helpers/buy';
import ApprovalDataCard from '../../components/BuyCreateCards/ApprovalDataCard';
import StatusBadge from '../../components/common/StatusBadge';
import ExpertDecisionCard from '../../components/project/PassportRead/ExpertDecisionCard';
import ConfirmModal from '../../components/common/ConfirmModal';
import { floatRenderer } from '../../services/formatSvc';
import { getBuyRemainders } from './helpers/remainders';
import BuyVersionHistoryModal from './BuyVersionHistoryModal';
import { getActionsModel } from './helpers/buyRoleStatus';
import useAPI from '../../hooks/useAPI';
import BuyKvrs from '../../components/BuyCreateCards/BuyKvrs';
import BuyDivisions from '../../components/BuyCreateCards/BuyDivisions';
import { getPathType, getProjectTypeText } from '../../helpers/typeProject';

export default function BuyEditForm() {
  const {
    stratId,
    policyId,
    projId,
    portfolioId,
    buyId,
  } = useParams(); // projId = originId
  const api = useAPI();
  const history = useHistory();
  const userCtx = useUserContext();
  const navProject = useNavProject();
  const addToast = useToast();

  const [proj, setProj] = useState({});
  const [validated, setValidated] = useState(false);
  const formEl = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      const invalidEl = formEl.current.querySelector(':invalid');
      invalidEl.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
    } else if (!buy.buyKvrs || buy.buyKvrs.length === 0) {
      addToast.error('Необходимо указать оценочную стоимость закупки');
    } else {
      await saveBuy();
    }
    setValidated(true);
  };

  const separateSaveMethod = async (whom) => {
    if (formEl.current.checkValidity() === false) {
      const invalidEl = formEl.current.querySelector(':invalid');
      invalidEl.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
    } else if (!buy.buyKvrs || buy.buyKvrs.length === 0) {
      addToast.error('Необходимо указать оценочную стоимость закупки');
    } else {
      await saveBuy(true, whom);
    }
    setValidated(true);
  };

  const [remainders, setRemainders] = useState([]);
  const [invalidateBuyDivisions, setInvalidateBuyDivisions] = useState(false)
  const saveBuy = async (isSend, whom) => {
    if (canEdit()) {
      const res = await api.post(`/buys/${buy.id}`, { ...buy, divisions: buyDivisions.data });
      if (res.id) {
        let buyDivivsionsSuccess = validateBuyDivisions()
        addToast.success('Закупка успешно сохранена');
        history.replace(`/${getPathType(proj?.strategy?.type)}/${stratId}${portfolioId ? `/portfolios/${portfolioId}` : ''}/projs/${projId}/buys/${res.id}`);
        setInvalidateBuyDivisions(prevState => !prevState)
        await getBuy(res.id);
        await validateRemainders(res.id);
        if (isSend && await validateRemainders(res.id) && buyDivivsionsSuccess) {
          if (whom === 'strat') {
            setIsModalSendToStrat(true);
          } else if (whom === 'portf') {
            setIsModalSendToPortf(true);
          }
        }
      }
    }
  };

  const validateRemainders = async (id) => {
    const onProj = buy.state === BST.onProj || buy.state === BST.onProjReturned || buy.state === BST.onProjNewVersion;
    const onPortf = buy.state === BST.onPortf;
    const onStratSf = (buy.state === BST.onStrat || buy.state === BST.onStratReturned) && buy.type === 'sf';
    const onStratFb = (buy.state === BST.onStrat || buy.state === BST.onStratReturned) && buy.type === 'fb';

    const rems = onProj || onPortf || onStratSf // на непревышение плана по проекту проверяем при отправке от РП, от РПортф + от РСП, если закупка СФ
      ? await getBuyRemainders(buy.year, projId, buy.id || id, buy.type === 'sf')
      : onStratFb
        ? await getBuyRemainders(buy.year, null, buy.id || id)
        : null;
    if (!rems) return true;
    if (rems.error) {
      addToast.error(rems.error);
      return false;
    }
    if (rems.values) {
      setRemainders(rems.values);
      if (rems.values.length > 0) {
        scroller.scrollTo('RemainderAlert', stepScroll);
        return false;
      }
      return true;
    }
    return false;
  };

  const validateBuyDivisions = () => {
    let buyDivisionsError = true;
    Object
      .entries(buyDivisions.metadata)
      .map(([_, valueByYear]) => ({
        year: valueByYear.year,
        remain: +(valueByYear.totalKvrs - valueByYear.total).toFixed(2)
      }))
      .forEach((valueByYear, index, array) => {
        if(valueByYear.remain !== 0){
          buyDivisionsError = false;
          const formattedValue = new Intl.NumberFormat('ru', {minimumFractionDigits: 2}).format(Math.abs(valueByYear.remain));
          addToast.error(valueByYear.remain > 0 ?
              `Необходимо распределить по институтам всю сумму закупки за ${valueByYear.year} год. Нераспределённый остаток ${formattedValue} руб.` :
              `Сумма, распределённая по институтам, превышает оценочную стоимость закупки за ${valueByYear.year} год на ${formattedValue} руб. Скорректируйте сумму принадлежности закупки к институтам»`,
            {delay: 5700 * array.length}
          );
        }
      })
    return buyDivisionsError;
  }

  const [projPlans, setProjPlans] = useState([]);
  const [approvedProjVersionInfo, setApprovedProjVersionInfo] = useState({
    year: null,
    planTypes: []
  });
  const [isHiddenSP, setIsHiddenSP] = useState(false);
  const [buy, setBuy] = useState({
    id: buyId !== 'new' ? parseInt(buyId) : 0,
    version: 1,
    status: 'notApproved',
    state: 'onProj',
    type: 'fb',

    projId: parseInt(projId),

    num: undefined,
    name: '',

    year: new Date().getFullYear(),
    agreementId: null,
    bcCodeId: null,
    sfSourceId: null,
    fundingId: null,

    projResults: [],

    expenseTypeId: null,

    buyKvrs: [],
    buyDivisions: []
  });
  const [teos, setTeos] = useState([]);
  const [buyDivisions, setBuyDivisions] = useState({
    data: [],
    metadata: {}
  });
  const [projExpertDecision, setProjExpertDecision] = useState({});

  const can = useMemo(
    () => getActionsModel(buy.status, buy.state, userCtx, stratId, projId, buy.portfId, buy.policyId),
    [buy.status, buy.state, userCtx, stratId, projId, buy.portfId, buy.policyId],
  );

  const getBuy = async (id) => {
    const resBuy = await api.get(`/buys/${id}?projId=${projId}`);
    if (!resBuy.error) {
      if (resBuy.ctx.projPlans.length === 0) {
        addToast.error('У данного проекта не указаны планы по финансированию');
      } else {
        setApprovedProjVersionInfo((prev) => ({
          year: resBuy.ctx.approvedYear,
          planTypes: resBuy.ctx.approvedPlanTypes
        }));
        setProjPlans((currentPlans) => resBuy.ctx.projPlans);
        if (resBuy.buy.id) {
          // доп.обработка для полей формы решения добавлена,
          // чтобы введенные них данные не затирались, если пользователь перепутал кнопки
          // и вместо кнопок на форме решения нажал "Сохранить"
          setBuy((currentBuy) => ({
            ...resBuy.buy,
            approvalType: resBuy.buy.approvalType ?? currentBuy.approvalType,
            recommendations: resBuy.buy.recommendations ?? currentBuy.recommendations,
            approvalDate: (resBuy.buy.approvalDate ?? currentBuy.approvalDate)?.substring(0, 10),
            approvalNum: resBuy.buy.approvalNum ?? currentBuy.approvalNum,
            approvalFile: resBuy.buy.approvalFile ?? currentBuy.approvalFile,
          }));
          setTeos(resBuy.teos || []);
        } else {
          const plans = resBuy.ctx.projPlans;
          const type = [...new Set(plans.map((t) => t.type))].length === 1 ? plans[0].type : 'fb';
          // если план в проекте только один, или в проекте нет плана за тот год субсидии, который у закупки по умолчанию (текущий)
          if (plans.length === 1 || !plans.find((p) => p.year === buy.year)) {
            // то выбираем из всех доступных для выбора планов проекта в качестве значения по умолчанию тот, где год минимальный
            const minPlan = plans.sort((p1, p2) => p1.year - p2.year)[0];
            setBuy({
              ...buy,
              type,
              portfId: buy.portfId ?? portfolioId,
              year: minPlan.year,
              agreementId: minPlan.agreementId,
              bcCodeId: minPlan.bcCodeId,
              fundingId: minPlan.fundingId,
              sfSourceId: minPlan.sfSourceId,
            });
          } else {
            // в противном случае присваиваем закупке тип (по умолчанию идет "грант", но могут быть проекты только с "сф")
            setBuy({
              ...buy,
              type,
              portfId: buy.portfId ?? portfolioId,
            });
          }
        }
      }
    }
  };

  // set navigation
  useEffect(async () => {
    const respProjectStructure = await api.get(`/structure/projs/${projId}?latest=true`);
    setProj(respProjectStructure);
    if (respProjectStructure.strategy) {
      navProject.setStratTitle(respProjectStructure.strategy.name);
      setIsHiddenSP(respProjectStructure.strategy.isHidden);
    } else if (respProjectStructure.policy) {
      navProject.setPolicyTitle(respProjectStructure.policy.name);
    }
    if (portfolioId) {
      navProject.setPortfolioTitle(respProjectStructure.portfolio.name);
      navProject.setPortfProjTitle(respProjectStructure.name);
    } else {
      navProject.setProjTitle(respProjectStructure.name);
    }
  }, []);

  useEffect(async () => {
    const decision = await api.get(`/projs/${projId}/expertDecision?latest=true`);
    if (decision) setProjExpertDecision(decision);

    await getBuy(buy.id);
  }, []);

  const onBuyFieldValueUpdate = (value, field) => {
    if (canEdit()) {
      setBuy((currentBuy) => ({
        ...currentBuy,
        [field]: value
      }));
    }
  };

  // todo: переключить нормально на getActionsModel
  const canEdit = () =>
    // РП может редактировать только на своей стадии
    ((buy.state === BST.onProj || buy.state === BST.onProjReturned || buy.state === BST.onProjNewVersion) && isProjectAdmin(userCtx, projId))
    // РСП - на всех, кроме "У секретаря"
    || (buy.state && buy.state !== BST.onSecy && isStratAdmin(userCtx, stratId))
    // РПортф - на всех, кроме "У секретаря" и "У руководителя СП"
    || (buy.state && buy.state !== BST.onSecy && buy.state !== BST.onStrat && buy.state !== BST.onStratReturned && isPortfolioAdmin(userCtx, buy.portfId ?? portfolioId))
    // ПО - всегда
    || isAdmin(userCtx);
  const [buyHistoryModal, setBuyHistoryModal] = useState(null);
  const [isModalSendToPortf, setIsModalSendToPortf] = useState(false);
  const [isModalSendToStrat, setIsModalSendToStrat] = useState(false);
  const [isModalSendToSecy, setIsModalSendToSecy] = useState(false);
  const [isModalReturnToProj, setIsModalReturnToProj] = useState(false);
  const [isModalRequestEdit, setIsModalRequestEdit] = useState(false);
  const [isModalChangeType, setIsModalChangeType] = useState({
    show: false,
    value: null
  });

  const isBuyDivisionsHidden = useMemo(() => (buy.expenseTypeId === 4 && buy.type === 'fb') ||
    (buy.buyKvrs.every(buyKvr => ['111.0', '119.0', '111.7', '119.7'].includes(buyKvr.costKvr?.num) && buy.type === 'sf')), [buy])

  const actions = {
    requestEdit: async () => setIsModalRequestEdit(true),
    saveAndSendToPortf: async () => separateSaveMethod('portf'),
    saveAndSendToStrat: async () => separateSaveMethod('strat'),
    sendToPortf: async () => {
      if (await validateRemainders()) setIsModalSendToPortf(true);
    },
    sendToStrat: async () => {
      if (await validateRemainders()) setIsModalSendToStrat(true);
    },
    returnToProj: () => setIsModalReturnToProj(true),
    sendToSecy: async () => {
      if (await validateRemainders()) {
        if (!stratId && buy.policyId) {
          setIsModalSendToSecy(true);
        } else {
          await apiSend('sendToSecy', 'Успешно отправлена секретарю Проектного офиса');
        }
      }
    },
    sendTo1S: async () => handleSendTo1S(),
    viewVersions: () => setBuyHistoryModal({
      id: buy.id,
      num: buy.num,
      version: buy.version,
      projId: buy.projId,
      stratId,
      policyId
    }),
  };

  const onTypeChange = async (type) => {
    setIsModalChangeType({
      show: true,
      value: type
    });
  };

  const handleTypeChange = () => {
    setBuy((prev) => ({
      ...prev,
      type: isModalChangeType.value,
      agreementId: null,
      bcCodeId: null,
      fundingId: null,
      sfSourceId: null,

      expenseTypeId: null,
      buyKvrs: [],
    }));
    setIsModalChangeType({
      show: false,
      value: null
    });
  };

  const handleNewVersion = async () => {
    const newVersion = await api.post(`/buys/${buy.id}/initVersion`);
    if (!newVersion.error) {
      setBuy((prev) => ({ ...prev, ...newVersion.buy }));
      setTeos((prev) => (newVersion.teos || []));
      history.push(stratId
        ? `/strats/${stratId}${portfolioId ? `/portfolios/${portfolioId}` : ''}/projs/${projId}/buys/${newVersion.buy.id}`
        : policyId
          ? `/policies/${policyId}/projs/${projId}/buys/${newVersion.buy.id}`
          : '');
      addToast.success('Создана новая версия закупки');
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      addToast.error(newVersion?.error?.message || 'При создании новой версии произошла ошибка');
    }
    setIsModalRequestEdit(false);
  };

  const apiSendToPortf = () => apiSend('sendToPortf', 'Успешно отправлена руководителю Портфеля');
  const apiSendToStrat = () => apiSend('send', `Успешно отправлена руководителю ${getProjectTypeText(proj?.strategy?.type)}`);
  const apiSendToSecy = () => apiSend('sendToSecy', 'Успешно отправлена секретарю');

  const apiSend = async (action, successComment) => {
    const response = await api.post(`/buys/${buy.id}/${action}`);
    if (!response.error && response.status) {
      setBuy((prev) => ({ ...prev, ...response }));
      setIsModalSendToPortf(false);
      setIsModalSendToStrat(false);
      setIsModalSendToSecy(false);
      addToast.success(successComment);
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      addToast.error(response?.error?.message || 'Не удалось отправить закупку');
    }
  };

  const handleReturn = async (comment) => {
    const response = await api.post(`/buys/${buy.id}/returnToProj`, { comment });
    if (!response.error) {
      setBuy((prev) => ({ ...prev, ...response }));
      setIsModalReturnToProj(false);
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      addToast.error(response?.error?.message || 'Не удалось вернуть закупку на доработку');
    }
  };

  const handleApprove = async (decision) => {
    if (isAdmin(userCtx)) {
      if (buy.approvalType && buy.approvalDate && buy.approvalNum) {
        const approvedBuy = await api.postFormData(
          `/buys/${buy.id}/${decision}`, // approve or decline
          {
            approvalType: buy.approvalType,
            approvalDate: buy.approvalDate,
            approvalNum: buy.approvalNum,
            recommendations: buy.recommendations || null,
          },
          buy.approvalFile || null,
        );
        if (!approvedBuy || approvedBuy.error) {
          addToast.error(approvedBuy?.error.message || 'Не удалось завершить операцию');
        } else {
          addToast.success(`Закупка ${approvedBuy.status === BS.approved ? 'утверждена' : 'отклонена'}`);
          setBuy((prev) => ({
            ...prev,
            ...approvedBuy,
            approvalDate: approvedBuy.approvalDate ? approvedBuy.approvalDate.substring(0, 10) : null,
          }));
        }
      } else {
        addToast.error('Необходимо заполнить реквизиты подтверждающего документа');
      }
    }
  };

  const handleEditDecision = async () => {
    if (isAdmin(userCtx)) {
      const approvedBuy = await api.postFormData(
        `/buys/${buy.id}/saveDecision`,
        {
          recommendations: buy.recommendations || null,
          approvalFileId: buy.approvalFileId, // если док не удален, то нужно передать его id
        },
        buy.approvalFile,
      );
      if (!approvedBuy || approvedBuy.error) {
        addToast.error(approvedBuy?.error.message || 'Не удалось завершить операцию');
      } else {
        addToast.success('Решение обновлено');
        setBuy((prev) => ({ ...prev, ...approvedBuy }));
      }
    }
  };

  const handleSendTo1S = async () => {
    if (buy.status === BS.approved && buy.state === BST.none) {
      const result = await api.post(`/buys/${buy.id}/sendTo1S`, { id: buy.id });
      if (!result || result.error) {
        addToast.error(result?.error?.message || 'Не удалось отправить закупку в 1С');
      } else {
        addToast.success('Закупка успешно отправлена в 1С');
      }
    }
  };

  return (
    <>
      <section className="page form-page project-card-page">
        <div className="container">
          <div style={{ marginBottom: 16 }}>
            <BackBtn/>
          </div>
          <nav className="step-scroll-box">
            <Link to="MainInfo" {...stepScroll}>Основная информация</Link>
            <Link to="BuyProjResults" {...stepScroll}>Уникальные результаты проекта</Link>
            <Link to="BuyKvrs" {...stepScroll}>Оценочная стоимость закупки</Link>
            {!isBuyDivisionsHidden && <Link to="BuyDivisions" {...stepScroll}>Принадлежность закупки к структурным подразделениям</Link>}
          </nav>

          <div style={styledContainer}>
            <h3 style={styledHeading}>
              Закупка {buy.num || ''}
            </h3>
            <div>
              <StatusBadge type="buy" status={buy.status}/>
              &nbsp;
              <StatusBadge
                type="buyState"
                status = {
                  proj?.strategy?.type && ['onStrat', 'onStratReturned'].includes(buy.state)
                    ? buy.state.replace('Strat', 
                        proj.strategy.type.charAt(0).toUpperCase() + proj.strategy.type.slice(1))
                    : buy.state
                }
              />
            </div>
          </div>

          {buy.comment && (
            <Alert variant="danger">
              <strong>Комментарий</strong>
              <br/>
              <br/>
              {buy.comment}
            </Alert>
          )}
          <Element id="RemainderAlert" name="RemainderAlert">
            {remainders && remainders.length > 0 && <Alert variant="danger">{remainders.map((r) => r)}</Alert>}
          </Element>

          <Form noValidate validated={validated} onSubmit={handleSubmit} ref={formEl}>
            <Element id="MainInfo" name="MainInfo">
              <MainInfo
                data={{
                  buy,
                  projPlans
                }}
                onChange={(value, field) => onBuyFieldValueUpdate(value, field)}
                onTypeChange={(type) => onTypeChange(type)}
                showHist={actions.viewVersions}
                readOnly={!canEdit()}
              />
            </Element>
            <Element id="BuyProjResults" name="BuyProjResults">
              <BuyProjResults
                buy={buy}
                onChange={(value, field) => onBuyFieldValueUpdate(value, field)}
                readOnly={!canEdit() || (BS.approved === buy.status || BS.declined === buy.status)}
              />
            </Element>
            <Element id="BuyKvrs" name="BuyKvrs">
              {buy.type === 'fb'
                && (
                  <BuyKvrs
                    buy={buy}
                    showAll={isHiddenSP}
                    onChange={(value, field) => onBuyFieldValueUpdate(value, field)}
                    readOnly={!canEdit() || (BST.none === buy.state && !isAdmin(userCtx))}
                  />
                )}
              {buy.type === 'sf'
                && (
                  <BuyKvrsSf
                    buy={buy}
                    onChange={(value, field) => onBuyFieldValueUpdate(value, field)}
                    readOnly={!canEdit() || (BST.none === buy.state && !isAdmin(userCtx))}
                  />
                )}
            </Element>
            {!isBuyDivisionsHidden && <Element id="BuyDivisions" name="BuyDivisions">
              <BuyDivisions
                buy={buy}
                onChange={value => setBuyDivisions(value)}
                invalidateTrigger={invalidateBuyDivisions}
              />
            </Element>}
            {!!buy.id
              && (
                <Element id="BuyTeos" name="BuyTeos">
                  <BuyTeos
                    buyId={buy.id}
                    files={teos}
                    onChange={(value) => setTeos(value)}
                    readOnly={!canEdit() || (BST.none === buy.state && !isAdmin(userCtx))}
                  />
                </Element>
              )}
            <ExpertDecisionCard
              className="mb-4"
              readOnly
              proj={projExpertDecision}
              cardTitle="Решение экспертной группы по проекту"
            />
            {(BST.onSecy === buy.state || BST.none === buy.state && (BS.approved === buy.status || BS.declined === buy.status))
              && (
                <ApprovalDataCard
                  className="mb-4"
                  readOnly={!isAdmin(userCtx)}
                  editOnly={BST.none === buy.state && (BS.approved === buy.status || BS.declined === buy.status)}
                  onChange={(value, field) => onBuyFieldValueUpdate(value, field)}
                  handleSubmit={() => handleApprove('approve')}
                  handleDecline={() => handleApprove('decline')}
                  handleEdit={handleEditDecision}
                  buy={buy}
                />
              )}
            <BuyFormButtons
              buy={buy}
              can={can}
              readOnly={projPlans.length === 0}
              hasApprovedPlans={new Date().getFullYear() === approvedProjVersionInfo.year && approvedProjVersionInfo.planTypes.includes(buy.type)}
              actions={actions}
              strategyType={proj?.strategy?.type}
            />
          </Form>
        </div>
      </section>
      <ModalSendForRevision
        show={isModalReturnToProj}
        onSubmit={(comment) => handleReturn(comment)}
        onClose={() => setIsModalReturnToProj(false)}
        headerText="Возврат на доработку"
        label="Комментарий"
      />
      <ConfirmModal
        show={isModalSendToPortf}
        onClose={() => setIsModalSendToPortf(false)}
        onConfirm={apiSendToPortf}
        headerText="Отправить на согласование"
        message="Закупка будет отправлена на согласование руководителю Портфеля, убедитесь, что все поля заполнены верно. Подтвердить отправку?"
      />
      <ConfirmModal
        show={isModalSendToStrat}
        onClose={() => setIsModalSendToStrat(false)}
        onConfirm={apiSendToStrat}
        headerText="Отправить на согласование"
        message={`Закупка будет отправлена на согласование руководителю ${getProjectTypeText(proj?.strategy?.type)}, убедитесь, что все поля заполнены верно. Подтвердить отправку?`}
      />
      <ConfirmModal
        show={isModalSendToSecy}
        onClose={() => setIsModalSendToSecy(false)}
        onConfirm={apiSendToSecy}
        headerText="Отправить секретарю"
        message="Закупка будет отправлена на рассмотрение секретарю Комиссии по оперативному решению вопросов по расходованию средств программ УрФУ, убедитесь, что все поля заполнены верно. Подтвердить отправку?"
      />
      <ConfirmModal
        show={isModalRequestEdit}
        onClose={() => setIsModalRequestEdit(false)}
        onConfirm={() => handleNewVersion()}
        headerText="Создание версии закупки"
        message="При сохранении будет создана новая версия закупки, она будет переведена в состояние «На доработке у руководителя проекта». Сохранить изменения?"
      />
      <ConfirmModal
        show={isModalChangeType.show}
        onClose={() => setIsModalChangeType({
          show: false,
          value: null
        })}
        onConfirm={() => handleTypeChange()}
        headerText="Изменения типа источника средств"
        message="При изменении источника финансирования введенные данные не сохранятся. Продолжить?"
      />
      {buyHistoryModal && <BuyVersionHistoryModal buy={buyHistoryModal} onClose={() => setBuyHistoryModal(null)}/>}
    </>
  );
}

const styledHeading = {
  marginBottom: 24,
  textAlign: 'left'
};
const styledContainer = {
  display: 'flex',
  marginTop: 36,
  justifyContent: 'space-between'
};
